/* =========================================================================
   Entry point. Mounts the app DOM, wires global input, runs the splash, boots
   auth, and registers the service worker. Guest mode is fully playable with no
   backend; sign-in lights up persistence.
   ========================================================================= */
import './styles.css';
import { appShellHTML } from './screens/appShell';
import { byId, isVisible, isModalOpen } from './dom';
import { unlockAudio } from './audio';
import { buildWheel, spinWheel } from './minigames/wheel';
import { skipManna } from './minigames/manna';
import { skipMatch3 } from './minigames/match3';
import { skipMary } from './minigames/maryMatch3';
import { startGame, startOrResume, nextQuestion, pickDifficulty, startNextRound, closeSourcePop, isSourcePopOpen, useHint } from './gameController';
import { renderResumeCta } from './resumeGame';
import { wireAuth, showWelcome, showHome } from './auth';
import { tryRestoreSession, getProfileMe, isAuthenticated } from './api';
import { initTelemetry, track, setTrackObserver } from './telemetry';
import { setOnScreenShown } from './dom';
import { initArcade, playArcade, isArcadeUnlocked, type ArcadeGame } from './arcade';
import { initModeSwitcher } from './ui/modeSwitcher';
import { isFriendlyPlayEnabled } from './featureFlags';
import { openFriendlyPlay } from './friendlyController';
import { initTutorials } from './tutorialOverlay';
import { initServedLog } from './servedLog';
import { initAccountMenu, syncAccountMenu } from './accountMenu';
import { initHomeFab, syncHomeFab } from './homeFab';
import './characters/theo'; // side-effect: register Brother Theo at boot
import { initCharacterArt, renderCompanionHome, renderNextRoundTheo } from './characters/ui';
import { resolvePlayerName } from './characters/name';
import { initCandlePrayer, openCandlePrayer } from './candlePrayer';
import { initQuests, questEvent, setQuestChangeObserver } from './quests';
import { renderJourneyHome } from './journey';
import { registerOpener } from './screens/screenRegistry';
import { triggerGtkyMoment, triggerNameAsk, nameAsked } from './gtky';
import { setGtkyEngagement } from './gtkyCadence';
import { initFeast, openFeastMoment } from './feast';
import { initCollections, openCollections } from './collections';
import { initPilgrimage, openPilgrimage, setPilgrimageChangeObserver } from './pilgrimage';
import { renderPathHome } from './pathHome';
import { initMonastery, openMonastery } from './monastery';
import { initLeaderboard, openLeaderboard } from './leaderboard';
import { initFriendlyPlay, friendlyAnswerByKey, friendlyAdvance } from './friendlyController';
import { recordQuestionAnswered, hasSeenDeepModeUnlock, setDeepModePending, setAuthedHistory, authedWithHistoryFromStats, isTheoIntroduced, migrateTheoIntroduced } from './progression';
import { onQuestEvent } from './quests';
import { applyHomeTiers, maybeFireDeepModeUnlock } from './homeGate';

function mount(): void {
  const root = byId('app');
  root.innerHTML = appShellHTML;
}

function wireInput(): void {
  // any tap unlocks/resumes audio (iOS suspends the context aggressively)
  document.addEventListener('pointerdown', unlockAudio, { passive: true });

  byId('startBtn').addEventListener('click', startOrResume); // resumes a saved run when one exists
  byId('startFreshLink').addEventListener('click', startGame); // explicit fresh start (clears the snapshot)
  byId('playAgainBtn').addEventListener('click', startGame);
  byId('resContinueBtn').addEventListener('click', startGame); // top-of-results onward CTA
  byId('beginRoundBtn').addEventListener('click', () => nextQuestion());
  byId<HTMLButtonElement>('spinBtn').onclick = spinWheel; // onclick only — never addEventListener (double-fires)

  byId('mannaSkip').onclick = () => skipManna();
  byId('match3Skip').onclick = () => skipMatch3();
  byId('marySkip').onclick = () => skipMary();

  document.querySelectorAll<HTMLButtonElement>('.nr-choice').forEach((b) =>
    b.addEventListener('click', () => pickDifficulty(b)),
  );
  byId('nrStartBtn').addEventListener('click', () => startNextRound());

  byId('resArcadeBtn').addEventListener('click', showHome); // results → home (Arcade lives there)
  initArcade();       // wire the home Arcade tiles + initial lock state
  // The home lockup's "Trivia ⌄" switcher. Deps are injected so ui/modeSwitcher
  // stays a leaf and can never form a cycle with arcade/friendlyController.
  initModeSwitcher({
    isArcadeUnlocked,
    isFriendlyEnabled: isFriendlyPlayEnabled,
    playArcade: (g) => playArcade(g as ArcadeGame),
    openFriendlyPlay,
  });
  initAccountMenu();  // wire the persistent account avatar/dropdown
  initHomeFab();      // wire the persistent bottom-left Home button
  initTutorials();    // mini-game (i) re-openers + tap-anywhere dismiss

  // v1.1.0 content surfaces: wire each screen's own controls, then the home
  // hub tiles that open them (guests included).
  initFeast();
  initCandlePrayer();  // wire the memorial candle overlay (chrome/dismiss)
  initCollections();
  initPilgrimage();
  initMonastery();
  initLeaderboard();
  initFriendlyPlay(); // study picker + untimed card (flag-gated entry points)
  byId('tilePilgrimage').addEventListener('click', () => void openPilgrimage());
  byId('tileMonastery').addEventListener('click', () => void openMonastery());
  byId('tileCollections').addEventListener('click', () => void openCollections());
  byId('tileLeaderboard').addEventListener('click', () => void openLeaderboard());
  // Home rules-card "What is the Pilgrimage?" explainer → open the map (Tier 1).
  byId('pilgExplainOpen').addEventListener('click', () => void openPilgrimage());
  // Path v1: "See the whole path →" under the station CTA opens the same map.
  byId('pathMapLink').addEventListener('click', () => void openPilgrimage());
  // The station CTA advances as gates tick during normal play — repaint home
  // whenever the engine's frontier or a gate counter moves.
  setPilgrimageChangeObserver(() => {
    if (isVisible('home')) renderPathHome();
  });
  // Theo's greeting-card feast sub-line is a tappable shortcut into the moment.
  document.addEventListener('click', (e) => {
    const t = e.target as HTMLElement | null;
    const line = t && t.closest('#theoFeastLine');
    if (line && (line.textContent || '').trim()) void openFeastMoment();
  });

  byId('sourcePop').addEventListener('pointerdown', closeSourcePop); // tap anywhere dismisses

  byId('hintBtn').addEventListener('click', useHint); // Theo's 50/50 (once per round)

  // Memorial candle chip is re-rendered by renderCompanionHome, so wire via
  // delegation: tapping it opens the "Light a candle for a loved one" prayer
  // overlay (a reverent act, not a game gate). Repaint the home chip on close.
  document.addEventListener('click', (e) => {
    const t = e.target as HTMLElement | null;
    if (t && t.closest('#dailyChip')) {
      openCandlePrayer(() => renderCompanionHome());
    }
  });

  document.addEventListener('keydown', onKeydown);

  buildWheel();
}

function onKeydown(e: KeyboardEvent): void {
  // A modal (round picker, streak sheet, milestone / rank-up moment, prayer
  // break, break card, leave sheet…) owns the keyboard: it handles its own
  // Enter/Escape, and the screen BEHIND it must not react — Enter used to
  // close a results moment and instantly start a new game, or launch a second
  // startOrResume() under a Round-1 picker opened from home.
  if (isModalOpen()) return;
  const isEnter = e.key === 'Enter' || e.key === ' ';
  // Enter/Space handled here — prevent native activation of a focused button (double-fire).
  // But never swallow typing inside the auth form inputs.
  const target = e.target as HTMLElement | null;
  const typing = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA');
  if (isEnter && !typing) e.preventDefault();

  // source popover swallows all keys
  if (isSourcePopOpen()) {
    if (isEnter || e.key === 'Escape') closeSourcePop();
    return;
  }

  if (typing) return; // let the auth forms handle their own Enter (submit)

  if (isVisible('play')) {
    const n = parseInt(e.key, 10);
    if (n >= 1 && n <= 4) {
      const b = byId('optionsGrid').querySelectorAll<HTMLButtonElement>('.opt')[n - 1];
      if (b) b.click();
    }
    if (e.key === 'h' || e.key === 'H') useHint(); // Theo's 50/50 hotkey
  }
  // Friendly Play: same 1-4 answer keys, Enter advances the card / keeps studying.
  if (isVisible('friendlyplay')) {
    const n = parseInt(e.key, 10);
    if (n >= 1 && n <= 4) friendlyAnswerByKey(n);
    if (isEnter) friendlyAdvance();
  }
  if (isVisible('home') && isEnter) startOrResume(); // matches the visible Start/Resume button
  if (isVisible('roundintro') && isEnter) nextQuestion();
  if (isVisible('bonus') && isEnter && !byId<HTMLButtonElement>('spinBtn').disabled) byId('spinBtn').click();
  if (isVisible('manna') && isEnter && !byId<HTMLButtonElement>('mannaBtn').disabled) byId('mannaBtn').click();
  if (isVisible('match3') && isEnter && !byId<HTMLButtonElement>('match3Btn').disabled) byId('match3Btn').click();
  if (isVisible('marymatch3') && isEnter && !byId<HTMLButtonElement>('maryBtn').disabled) byId('maryBtn').click();
  if (isVisible('nextround') && isEnter) startNextRound(); // launches the current selection (Balanced by default)
  if (isVisible('results') && isEnter) startGame();
}

/* ---------- First-visit name ask (v1.1.0) ---------- */
let nameAskAttempted = false;
/** On the first home visit AFTER Theo has introduced himself, if we have no name
    to greet the player by and they haven't been asked yet, let Theo warmly ask
    (guests included). Never re-asks; authed users with a display name are already
    covered, so we skip them. Stage 0 (pre-introduction) returns WITHOUT consuming
    the once-per-session attempt — asking to know a pilgrim only makes narrative
    sense after he has met them. */
function maybeAskName(): void {
  if (nameAskAttempted) return;
  if (!isTheoIntroduced()) return; // stage 0: he hasn't earned his entrance yet
  nameAskAttempted = true;
  if (nameAsked()) return; // already answered or skipped in a prior session
  if (resolvePlayerName() !== '') return; // we already know a name (e.g. authed displayName)
  triggerNameAsk(() => {
    if (isVisible('home')) renderCompanionHome(); // re-greet with the new name
  });
}

/* ---------- Splash / intro ---------- */
const SPLASH_MS = 3200;
let splashDone = false;
function dismissSplash(): void {
  if (splashDone) return;
  splashDone = true;
  const sp = byId('screenSplash');
  const v = byId<HTMLVideoElement>('splashVideo');
  try {
    v.pause();
  } catch {
    /* noop */
  }
  sp.classList.add('fade');
  setTimeout(() => sp.classList.add('gone'), 550);
}
function runSplash(): void {
  const sp = byId('screenSplash');
  const v = byId<HTMLVideoElement>('splashVideo');
  const p = v.play && v.play();
  if (p && p.catch) p.catch(() => {});
  sp.addEventListener('pointerdown', () => {
    unlockAudio();
    dismissSplash();
  });
  v.addEventListener('ended', dismissSplash);
  setTimeout(dismissSplash, SPLASH_MS);
}

/* ---------- Service worker (production builds only — dev must never cache) ---------- */
function registerServiceWorker(): void {
  if (import.meta.env.PROD && 'serviceWorker' in navigator && location.protocol.startsWith('http')) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('sw.js').catch((err) => console.warn('SW registration failed:', err));
    });
  }
}

/** Factory-fresh device reset via `?fresh` — wipes every catholics.* key (guest id,
    name, unlocks, progression, snapshots, tokens) plus sessionStorage, then reloads
    without the param. For testing the true new-player arc and for shared devices. */
function maybeFreshReset(): boolean {
  if (!new URLSearchParams(location.search).has('fresh')) return false;
  try {
    for (const k of Object.keys(localStorage)) {
      // catholics.* = app state; ca_* = auth (refresh token) — both must go,
      // or a "fresh" device would silently auto-login the previous account.
      if (k.startsWith('catholics.') || k.startsWith('ca_')) localStorage.removeItem(k);
    }
    sessionStorage.clear();
  } catch {
    /* storage unavailable — nothing to wipe */
  }
  location.replace(location.pathname); // strip the param; boot clean
  return true;
}

async function boot(): Promise<void> {
  if (maybeFreshReset()) return; // page is reloading clean — don't double-boot
  mount();
  wireInput();
  wireAuth(startOrResume); // history's Play / ▶ Resume: a saved run resumes, like the home button
  registerServiceWorker();

  // Activity telemetry → /sadmin time-on-site metrics (guests + signed-in players).
  initTelemetry();
  initServedLog(); // page-hide keepalive flush for served-question codes

  // v1.1.0 quest engine: forward EVERY track() to quest evaluation via one shim,
  // re-render the pilgrimage card on any quest change, then load today's set.
  setTrackObserver(questEvent);
  setQuestChangeObserver(() => renderJourneyHome());
  void initQuests();

  // Progressive disclosure: bump the lifetime question counter off the SAME
  // event fan-out the quest engine uses (no second telemetry observer). When the
  // counter crosses the deep-mode threshold mid-play, arm a pending flag — the
  // next home render fires the one-time "The road opens" moment.
  onQuestEvent((ev) => {
    if (ev !== 'QuestionAnswered') return;
    const { crossed } = recordQuestionAnswered(1);
    if (crossed && !hasSeenDeepModeUnlock()) setDeepModePending(true);
  });

  // Journey Director CTA targets. The feast opener lights up the journey's feast
  // CTA (Wave 4 — this is the wave that ships it).
  registerOpener('play', () => startOrResume()); // a saved run resumes rather than restarting
  registerOpener('gtky', () => triggerGtkyMoment());
  registerOpener('arcade', () => {
    if (isArcadeUnlocked()) playArcade('wheel');
    else startOrResume();
  });
  registerOpener('feast', () => void openFeastMoment());

  setOnScreenShown((name) => {
    track('ScreenView', { screen: name });
    syncAccountMenu(name); // show/hide + refresh the account button per screen
    syncHomeFab(name); // show/hide the bottom-left Home button per screen
    if (name === 'home') {
      renderCompanionHome(); // Theo greeting card (throttled)
      applyHomeTiers(); // progressive-disclosure gating (before journey draws the card)
      renderJourneyHome(); // Today's Pilgrimage quest-log card (hidden at Tier 0)
      renderResumeCta(); // Start vs Resume-Round-N CTA (round-boundary snapshot)
      renderPathHome(); // Path v1 station CTA + meter + reward (AFTER the resume label)
      maybeAskName(); // first-visit warm name ask (once)
      maybeFireDeepModeUnlock(); // one-time "The road opens" if the counter just crossed
    }
    if (name === 'nextround') renderNextRoundTheo(); // celebratory Theo on "Well done!"
  });

  // Probe Theo's art/idle-loop; re-render the home card as each asset lands.
  initCharacterArt(() => {
    if (isVisible('home')) renderCompanionHome();
    if (isVisible('nextround')) renderNextRoundTheo();
  });

  // Restore a prior session if a refresh token survived; otherwise the welcome
  // screen (Play as Guest / Sign In / Register) is the entry point.
  const restored = await tryRestoreSession().catch(() => false);
  if (restored) {
    // Existing accounts are never re-gated: resolve server history BEFORE the
    // first home render so a returning player sees Tier 1 immediately (no flash).
    if (isAuthenticated()) {
      const pr = await getProfileMe().catch(() => null);
      if (pr && pr.ok) {
        setAuthedHistory(authedWithHistoryFromStats(pr.data.stats));
        setGtkyEngagement(pr.data.stats); // GTKY engaged-player gate (PLAN §6)
      }
    }
    migrateTheoIntroduced(); // existing players (games/history/questions) skip the staged intro
    showHome();
  } else {
    migrateTheoIntroduced(); // guests with prior device play are seeded too
    showWelcome();
  }

  // /sadmin "Test" deep-link (?tester=manna|match3|marymatch3|wheel): jump straight
  // into that mini-game as a pure QA sandbox (no credit, no ArcadePlayed telemetry —
  // see playArcade opts.tester). Skips the splash so testers land in the game at once.
  if (!maybeLaunchTester()) runSplash();
}

/** Launch the mini-game tester when ?tester=<game> is present. Returns true if launched. */
function maybeLaunchTester(): boolean {
  const raw = new URLSearchParams(location.search).get('tester');
  if (!raw) return false;
  const map: Record<string, ArcadeGame> = {
    manna: 'manna',
    match3: 'match3',
    marymatch3: 'maryMatch3',
    wheel: 'wheel',
  };
  const game = map[raw.toLowerCase()];
  if (!game) return false; // unknown key — normal boot
  dismissSplash(); // the splash overlay covers everything until dismissed — testers land straight in the game
  playArcade(game, { tester: true });
  return true;
}

void boot();
